import { HTTPClient, propsToDate, propsToDatePaged } from "@abaco/web-common/src/HTTP";
import { dbGIStoStandardPage, PagedResults, PagingParams } from "@abaco/web-common/src/Paging";
import { DashboardVariety, fromDashboardVarietyToVarietyBase, fromVarietyToVarietyBase, fromVegetativeCycleToVarietyBase, Variety, VarietyBase, VegetativeCycle } from "../models/Variety";
import { Block } from "@abaco/crop-plan-module/src/modules/crop-plan-module/models/Block";

export default class VarietiesApi {

    private http: HTTPClient;

    constructor(http: HTTPClient) {
        this.http = http;
    }

    public async getVarieties(pkCuaa: number, campaign: number, filter: string | null): Promise<VarietyBase[]> {
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/dashboard/subjects/${pkCuaa}/campaigns/${campaign}/varieties`, {
            params: {
                filter: filter ? `^variety like ${filter.toUpperCase()}` : undefined,
            }
        });
        const base = res.data.map((e: Variety) => fromVarietyToVarietyBase(e));
        return base;
    }

    public async getAllDashboardPlotVarieties(pkCuaa: number, campaign: number, plotId: number, filter: string | null): Promise<VarietyBase[]> {
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/dashboard/subjects/${pkCuaa}/campaigns/${campaign}/varieties_v2?plot_id=${plotId}`, {
            params: {
                filter: filter ? `^seed_name like ${filter.toUpperCase()}` : undefined,
            }
        });
        const base = res.data.varieties.map((e: DashboardVariety) => fromDashboardVarietyToVarietyBase(e));
        return base
    }

    public async getPlotVarieties(pkCuaa: number, campaign: number, plotId: number, filter: string | null, paging: PagingParams): Promise<PagedResults<VegetativeCycle>> {
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/plots/plots/${plotId}/vegetative_cycles`, {
            params: {
                filter: filter ? `^seed_name like ${filter.toUpperCase()}` : undefined,
                offset: paging.offset,
                count: paging.count,
            }
        });
        return res.data;
    }

    public async getAllPlotVarieties(pkCuaa: number, campaign: number, plotId: number, filter: string | null): Promise<VarietyBase[]> {
        let count = 0;
        let res: VegetativeCycle[] = [];
        let totalcount = 0;
        do {
            const curPage = await this.getPlotVarieties(pkCuaa, campaign, plotId, filter, { offset: count, count: 300 });
            count += curPage.count;
            totalcount = curPage.totalcount;
            res = [...res, ...curPage.records];
        } while (count < totalcount)
        const base = res.map((e: VegetativeCycle) => fromVegetativeCycleToVarietyBase(e));
        return base;
    }

    public async getBlockVarieties(pkCuaa: number, campaign: number, plotId: number, blockId: number, filter: string | null, paging: PagingParams): Promise<PagedResults<VegetativeCycle>> {
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/plots/plots/${plotId}/blocks/${blockId}/vegetative_cycles`, {
            params: {
                filter: filter ? `^seed_name like ${filter.toUpperCase()}` : undefined,
                offset: paging.offset,
                count: paging.count,
            }
        });
        return res.data;
    }

    public async getAllBlockVarieties(pkCuaa: number, campaign: number, plotId: number, blockId: number, filter: string | null): Promise<VarietyBase[]> {
        let count = 0;
        let res: VegetativeCycle[] = [];
        let totalcount = 0;
        do {
            const curPage = await this.getBlockVarieties(pkCuaa, campaign, plotId, blockId, filter, { offset: count, count: 300 });
            count += curPage.count;
            totalcount = curPage.totalcount;
            res = [...res, ...curPage.records];
        } while (count < totalcount)
        const base = res.map((e: VegetativeCycle) => fromVegetativeCycleToVarietyBase(e));
        return base;
    }

    public async getBlocksOld(pkCuaa: number, campaign: number, plotId: number, search: string | null, paging: PagingParams): Promise<PagedResults<Block>> {
        const filter = [];
        if (search) {
            filter.push(`^block_code like ${search.toUpperCase()}`);
        }
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/plots/plots/${plotId}/blocks`, {
            params: {
                offset: paging.offset,
                filter: filter.length ? filter : undefined,
                count: paging.count,
                include_geometry: true,
                include_planting: true
            }
        })
        return res.data;
    }

    public async getBlocksOldOnlyFirstLevel(pkCuaa: number, campaign: number, plotId: number, search: string | null, paging: PagingParams): Promise<PagedResults<Block>> {
        const filter = [];
        filter.push(`block_level eq 1`);
        if (search) {
            filter.push(`^block_code like ${search.toUpperCase()}`);
        }
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/plots/plots/${plotId}/blocks`, {
            params: {
                offset: paging.offset,
                filter: filter.length ? filter : undefined,
                count: paging.count,
                include_geometry: true,
                include_planting: true
            }
        })
        return res.data;
    }

    public async getBlocks(pkCuaa: number, campaign: number, search: string | null, paging: PagingParams): Promise<PagedResults<Block>> {
        const filter = [];
        if (search) {
            filter.push(`^block_code like ${search.toUpperCase()}`);
        }
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/plots/subjects/${pkCuaa}/${campaign}/blocks`, {
            params: {
                offset: paging.offset,
                filter: filter.length ? filter : undefined,
                count: paging.count,
                include_geometry: true,
                include_planting: true
            }
        })
        return res.data;
    }
    public async getPlotBlocks(pkCuaa: number, campaign: number, plotId: number, search: string | null, paging: PagingParams): Promise<PagedResults<Block>> {
        const filter = [];
        filter.push('plot_id eq ' + plotId);
        if (search) {
            filter.push(`^block_code like ${search.toUpperCase()}`);
        }
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/plots/subjects/${pkCuaa}/${campaign}/blocks`, {
            params: {
                offset: paging.offset,
                filter: filter.length ? filter : undefined,
                count: paging.count,
                include_geometry: true,
                include_planting: true
            }
        })
        return res.data;
    }

}
